// SPEC-28 · the lines of the recap: each visible screen and its answer, in
// the questionnaire's order. Which screens show and which fields belong to
// an answer are poweryourflow-lib's (visibleQuestions, fieldsOf); this file
// only puts the stored values into words.

import { fieldsOf, visibleQuestions } from "poweryourflow-lib";
import type { Question, Questionnaire } from "poweryourflow-lib";

import { copy } from "../i18n/pt-PT";
import type { QuestionText, QuestionnaireText } from "../i18n/pt-PT";
import type { AnswerValue, Answers } from "../schema/types";

const text = copy.questionnaire.recap;

export interface RecapLine {
  /** The screen's question id, which « Alterar » reopens. */
  readonly id: string;
  readonly question: string;
  readonly answer: string;
}

/** "8,5": a decimal comma, as pt-PT writes it. */
const decimal = (value: number) => value.toLocaleString("pt-PT");

const isEmpty = (value: AnswerValue | undefined) =>
  value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

/** One stored value in words: an option's label, labels joined, a scale, a number with its unit, a time, a text. */
function valueText(question: Question, value: AnswerValue, said: QuestionText | undefined): string {
  const label = (option: string) => said?.short?.[option] ?? said?.options?.[option] ?? option;
  switch (question.kind) {
    case "single":
      return label(String(value));
    case "multi":
      return (Array.isArray(value) ? value : []).map((option) => label(String(option))).join(" · ");
    case "scale":
      return text.scale(Number(value), question.max ?? 0);
    case "number":
      return said?.unit ? `${decimal(Number(value))} ${said.unit}` : decimal(Number(value));
    case "text":
      return String(value).trim() || text.noComment;
    default:
      return String(value);
  }
}

/** A field's value, after its recap word when the copy gives one (« intensidade 7 / 10 »). */
function fieldText(field: Question, value: AnswerValue, said: QuestionText | undefined): string {
  const shown = valueText(field, value, said);
  return said?.recap ? `${said.recap} ${shown}` : shown;
}

/** The answer of one screen, with the fields its choice opened. */
function answerOf(items: Questionnaire, answers: Answers, question: Question, words: QuestionnaireText): string {
  const said = words.questions[question.id];
  const value = answers[question.id];
  if (value === undefined) return "";
  const fields = fieldsOf(items, answers, question.id).filter((field) => !isEmpty(answers[field.id]));
  const partOf = (field: Question) => fieldText(field, answers[field.id]!, words.questions[field.id]);

  // A screen answered by its fields (Ciclo · sintomas): its fields are the answer.
  if (question.byFields) {
    if (value === question.byFields.none) return said?.recapNone ?? said?.none ?? "";
    return fields.map(partOf).join(" · ");
  }

  // « Outro » and the text under it read as one: « Outro (Patinagem) »; any other field follows after « · ».
  const head = valueText(question, value, said);
  const inBrackets = fields.filter((field) => field.kind === "text" && field.showIf?.anyOf?.includes("outro"));
  const bracket = inBrackets.map((field) => ` (${String(answers[field.id]).trim()})`).join("");
  const after = fields.filter((field) => !inBrackets.includes(field)).map(partOf);
  return [`${head}${bracket}`, ...after].join(" · ");
}

/** Every visible screen with its answer, in order; a skipped question has no line. */
export function recapLines(items: Questionnaire, answers: Answers, words: QuestionnaireText): RecapLine[] {
  return visibleQuestions(items, answers)
    .filter((question) => question.screen !== false)
    .map((question) => {
      const said = words.questions[question.id];
      const title = said?.title ?? question.id;
      return {
        id: question.id,
        question: said?.section ? text.inSection(said.section, title) : title,
        answer: answerOf(items, answers, question, words),
      };
    });
}
